import { ref } from 'vue';

import { useToast } from '@/components/ui/toast/use-toast';
import apiClient from '@/services/api_client';
import { readStream } from '@/services/sse';
import type { SlideStudioPageItem } from '@/types';

const toast = useToast();

export type PaletteEntry = { hex: string; weight?: number };

export type TypeRole = {
    role: string;
    tag?: string;
    size_pt: number;
    weight?: string;
    uppercase?: boolean;
    color?: string;
};

export interface DesignTokens {
    canvas?: Record<string, number>;
    palette?: {
        background?: PaletteEntry[];
        text?: PaletteEntry[];
        accent?: PaletteEntry[];
    };
    fonts?: { heading?: string; body?: string };
    typography?: TypeRole[];
    radius?: Record<string, unknown>;
    backgrounds?: unknown[];
    logo?: {
        light_bg?: string | null;
        dark_bg?: string | null;
        light_bg_color?: string | null;
        dark_bg_color?: string | null;
    };
}

export interface ThemeLogoFiles {
    light?: File;
    dark?: File;
}

export interface ThemeListItem {
    id: string;
    alias: string;
    visibility: string;
    design_tokens?: DesignTokens;
    cover_preview_html_content?: string;
    created_at?: string;
    updated_at?: string;
}

export async function getThemes(tenantId: string): Promise<ThemeListItem[]> {
    const url = `/slide_studio/themes?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getGlobalThemes(): Promise<ThemeListItem[]> {
    const url = `/slide_studio/themes/global`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getThemeById(
    themeId: string,
    tenantId: string,
    signal?: AbortSignal
): Promise<ThemeListItem> {
    const url = `/slide_studio/theme/${encodeURIComponent(themeId)}?tenant_id=${encodeURIComponent(tenantId)}`;
    const response = await apiClient.get(url, { signal });
    return await response.json();
}

export async function updateTheme(
    themeId: string,
    tenantId: string,
    theme: ThemeListItem,
    logoFiles?: ThemeLogoFiles
): Promise<ThemeListItem & { task_run_id?: string | null }> {
    const formData = new FormData();
    formData.append('alias', theme.alias);
    formData.append('visibility', theme.visibility);
    if (theme.design_tokens != null) {
        formData.append('design_tokens', JSON.stringify(theme.design_tokens));
    }
    if (logoFiles?.light) {
        formData.append('logo_light', logoFiles.light);
    }
    if (logoFiles?.dark) {
        formData.append('logo_dark', logoFiles.dark);
    }
    const url = `/slide_studio/theme/${encodeURIComponent(themeId)}?tenant_id=${encodeURIComponent(tenantId)}`;
    return await apiClient.postFormData(url, formData);
}

export async function deleteTheme(
    themeId: string,
    tenantId: string
): Promise<{ success: boolean }> {
    const url = `/slide_studio/theme/${encodeURIComponent(themeId)}?tenant_id=${encodeURIComponent(tenantId)}`;
    return await apiClient.delete(url, {});
}

export type SelectedSlideStudioSlide = {
    key: string;
    presentationId: string;
    slide: SlideStudioPageItem;
};

export async function getPaginatedSlidesByDocumentId(
    documentId: string,
    tenantId: string,
    offset: number = 0,
    limit: number = 10,
    signal?: AbortSignal
): Promise<{
    total: number;
    slides: SlideStudioPageItem[];
}> {
    const params = new URLSearchParams({
        tenant_id: tenantId,
        offset: String(offset),
        limit: String(limit)
    });
    const url = `/slide_studio/documents/${encodeURIComponent(documentId)}/slides?${params.toString()}`;
    const response = await apiClient.get(url, { signal });
    return await response.json();
}

export async function getSlideContentById(
    slideId: string,
    tenantId: string
): Promise<string> {
    const url = `/slide_studio/slides/${slideId}?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function deleteSlideById(slideId: string, tenantId: string) {
    const url = `/slide_studio/slides/${slideId}?tenant_id=${tenantId}`;
    return await apiClient.delete(url, {});
}

export async function moveSlide(
    slideId: string,
    newPosition: number,
    documentId: string,
    tenantId: string
): Promise<{ moved: boolean }> {
    const url = `/slide_studio/slides/${slideId}/move`;
    const body = {
        new_position: newPosition,
        document_id: documentId,
        tenant_id: tenantId
    };
    return await apiClient.patch(url, body);
}

export async function copySlidesToDocument(
    sourceSlideIds: string[],
    targetDocumentId: string,
    targetPosition: number,
    tenantId: string
): Promise<SlideStudioPageItem[]> {
    const url = `/slide_studio/slides/copy?tenant_id=${encodeURIComponent(tenantId)}`;
    return await apiClient.post(url, {
        source_slide_ids: sourceSlideIds,
        target_document_id: targetDocumentId,
        target_position: targetPosition
    });
}

export interface SlideStudioHistoryInfo {
    can_undo: boolean;
    can_redo: boolean;
}

export async function getSlideStudioHistoryInfo(
    documentId: string,
    tenantId: string,
    signal?: AbortSignal
): Promise<SlideStudioHistoryInfo> {
    const url = `/slide_studio/documents/${documentId}/history?tenant_id=${tenantId}`;
    const response = await apiClient.get(url, { signal });
    return await response.json();
}

export async function undoSlideStudioHistory(
    documentId: string,
    tenantId: string
): Promise<SlideStudioHistoryInfo> {
    const url = `/slide_studio/documents/${documentId}/undo`;
    return await apiClient.post(url, { tenant_id: tenantId });
}

export async function redoSlideStudioHistory(
    documentId: string,
    tenantId: string
): Promise<SlideStudioHistoryInfo> {
    const url = `/slide_studio/documents/${documentId}/redo`;
    return await apiClient.post(url, { tenant_id: tenantId });
}

async function uploadThemeChunks(
    file: File,
    tenantId: string,
    endpoint: string,
    options: {
        visibility?: string;
        totalFiles?: number;
        fileIndex?: number;
        sharedToast?: { id: string; update: (options: any) => void };
        onProgress?: (progress: number) => void;
    } = {}
): Promise<any> {
    const {
        visibility,
        totalFiles = 1,
        fileIndex = 1,
        sharedToast,
        onProgress
    } = options;
    const chunkSize = 1 * 1024 * 1024; // 1MB
    const totalChunks = Math.ceil(file.size / chunkSize);
    let offset = 0;
    let chunkNumber = 0;
    let filePath = undefined;
    let lastResponse: any = null;

    const toastInstance =
        sharedToast ||
        toast.toast({
            title: `Uploading document ${fileIndex}/${totalFiles}`,
            description: `${file.name} - Progress: 0%`,
            duration: Infinity
        });

    const updateProgress = (progress: number) => {
        if (onProgress) {
            onProgress(progress);
        } else if (!sharedToast) {
            toastInstance.update({
                id: toastInstance.id,
                title: `Uploading document ${fileIndex}/${totalFiles}`,
                description: `${file.name} - Progress: ${progress}%`
            });
        }
    };

    while (offset < file.size) {
        const chunk = file.slice(offset, offset + chunkSize);
        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('name', file.name);
        formData.append('chunk_number', String(chunkNumber));
        formData.append('total_chunks', String(totalChunks));
        formData.append('tenant_id', tenantId);
        if (visibility != null) {
            formData.append('visibility', visibility);
        }
        formData.append('file_path', filePath);

        try {
            const response = await apiClient.postFormData(endpoint, formData);
            filePath = response.file_path;
            lastResponse = response;
        } catch (error) {
            toastInstance.update({
                id: toastInstance.id,
                title: 'Upload Error',
                description: `Failed to upload ${file.name}.`,
                variant: 'destructive',
                duration: 2000
            });
            return null;
        }

        offset += chunkSize;
        chunkNumber++;
        const progress = Math.floor((offset / file.size) * 100);
        updateProgress(Math.min(progress, 100));

        await new Promise((resolve) => setTimeout(resolve, 0));
    }

    if (!sharedToast) {
        toastInstance.update({
            id: toastInstance.id,
            title: 'Upload Complete',
            description: `${file.name} uploaded. Processing theme…`,
            duration: 3000
        });
    }

    return lastResponse;
}

export async function createThemeFromUpload(
    file: File,
    tenantId: string,
    visibility: string = 'private',
    totalFiles: number = 1,
    fileIndex: number = 1,
    sharedToast?: { id: string; update: (options: any) => void },
    onProgress?: (progress: number) => void
): Promise<any> {
    return uploadThemeChunks(file, tenantId, '/slide_studio/theme', {
        visibility,
        totalFiles,
        fileIndex,
        sharedToast,
        onProgress
    });
}

export async function createGlobalThemeFromUpload(
    file: File,
    tenantId: string,
    totalFiles: number = 1,
    fileIndex: number = 1,
    sharedToast?: { id: string; update: (options: any) => void },
    onProgress?: (progress: number) => void
): Promise<any> {
    return uploadThemeChunks(file, tenantId, '/slide_studio/theme/global', {
        totalFiles,
        fileIndex,
        sharedToast,
        onProgress
    });
}

export interface ThemeProcessingCallbacks {
    onProgress?: (step: string) => void;
    onSuccess?: (data: {
        theme_id: string;
        alias: string;
        visibility: string;
    }) => void;
    onError?: (error: string) => void;
}

/**
 * Subscribe to theme processing progress via SSE.
 *
 * Connects to the generic /track_task/{task_run_id} SSE endpoint and
 * translates taskProgress events into typed callbacks.
 *
 * @returns An object with an `abort()` method to cancel the subscription.
 */
export function subscribeToThemeProcessing(
    taskRunId: string,
    callbacks: ThemeProcessingCallbacks
): { abort: () => void } {
    const abortController = new AbortController();
    const isSubscribed = ref(true);

    const connect = async () => {
        const reader = await apiClient.postSSERequest(
            `/track_task/${taskRunId}`,
            {},
            abortController.signal
        );

        if (!reader) {
            callbacks.onError?.('Failed to connect to processing stream');
            return;
        }

        readStream(reader, isSubscribed, (jsonData) => {
            if (!jsonData?.data) return;
            const data = jsonData.data;

            if (data.state === 'PROGRESS') {
                callbacks.onProgress?.(data.step);
            } else if (data.state === 'SUCCESS') {
                isSubscribed.value = false;
                callbacks.onSuccess?.({
                    theme_id: data.theme_id,
                    alias: data.alias,
                    visibility: data.visibility
                });
            } else if (data.state === 'ERROR') {
                isSubscribed.value = false;
                callbacks.onError?.(data.error);
            }
        });
    };

    connect();

    return {
        abort: () => {
            isSubscribed.value = false;
            abortController.abort();
        }
    };
}

export async function duplicateSlideDocument(
    documentId: string,
    tenantId: string
): Promise<{ document_id: string; name: string }> {
    const url = `/slide_studio/documents/${encodeURIComponent(documentId)}/duplicate?tenant_id=${encodeURIComponent(tenantId)}`;
    return await apiClient.post(url, {});
}

export type SlideExportFormat = 'pdf' | 'pptx';

export type SlideExportWarning = {
    code: string;
    slide_index?: number;
    element_type?: string;
    detail?: string;
    src?: string;
    path?: string;
    reason?: string;
    /** Scanner/runtime error text (e.g. SCANNER_ERROR). */
    message?: string;
};

export type SlideExportManifestItem = {
    export_id: string;
    tenant_id: string | null;
    tenant_name: string | null;
    document_id: string | null;
    document_name: string | null;
    format: SlideExportFormat | null;
    status: 'SUCCESS' | 'ERROR' | null;
    error: string | null;
    created_at: string | null;
    duration_s: number | null;
    slide_count: number | null;
    warnings: SlideExportWarning[];
};

// Cross-tenant, Elqano-admin only: lists the slide-export tracking manifests of
// ALL tenants (the backend reads them from the Elqano container's
// slide_export_tracking/ archive). `tenantId` is NOT a filter — it's the
// caller's own tenant, required by the Elqano-admin auth gate.
export async function listSlideExports(
    tenantId: string,
    limit = 50,
    offset = 0
): Promise<SlideExportManifestItem[]> {
    const url =
        `/slide_studio/exports?tenant_id=${encodeURIComponent(tenantId)}` +
        `&limit=${limit}&offset=${offset}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export type SlideExportResult = {
    export_id: string;
    format: SlideExportFormat;
    slide_count: number;
    warnings_count: number;
};

export type SlideExportCallbacks = {
    onProgress?: (info: {
        step: string;
        slideIndex?: number;
        total?: number;
    }) => void;
    onSuccess?: (result: SlideExportResult) => void;
    onError?: (error: string) => void;
};

export async function startSlideExport(
    documentId: string,
    tenantId: string,
    format: SlideExportFormat,
    slideIds?: string[]
): Promise<string> {
    const url = `/slide_studio/documents/${encodeURIComponent(documentId)}/export`;
    const response = await apiClient.post(url, {
        tenant_id: tenantId,
        format,
        slide_ids: slideIds ?? null
    });
    return response.task_run_id;
}
const EXPORT_STREAM_INACTIVITY_TIMEOUT_MS = 120_000;
// Queue wait + cold Chromium startup + a slow first slide can elapse before the
// very first progress event, with no SSE activity in between (Celery
// STARTED/DONE payloads are non-JSON and dropped by readStream). Give the first
// event a wider budget than the between-slides inactivity window so a
// healthy-but-slow start isn't killed as a spurious timeout. Tune alongside the
// export worker soft time limit.
const EXPORT_STREAM_FIRST_EVENT_TIMEOUT_MS = 300_000;

export function subscribeToSlideExport(
    taskRunId: string,
    callbacks: SlideExportCallbacks
): { abort: () => void } {
    const abortController = new AbortController();
    const isSubscribed = ref(true);
    // A terminal SSE event (SUCCESS/ERROR) settles the caller's promise. Track
    // whether one arrived and whether the caller aborted, so a stream that
    // closes early — or never emits at all — still surfaces an error instead of
    // hanging forever.
    let terminalReceived = false;
    let aborted = false;
    // The first event uses a wider budget (cold start); subsequent silence uses
    // the shorter between-slides inactivity window.
    let firstEventSeen = false;

    // Inactivity watchdog: rearmed on every received event, fired when the
    // stream goes silent for too long (see EXPORT_STREAM_INACTIVITY_TIMEOUT_MS).
    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    const clearInactivityTimer = () => {
        if (inactivityTimer !== null) {
            clearTimeout(inactivityTimer);
            inactivityTimer = null;
        }
    };
    const onInactivityTimeout = () => {
        if (terminalReceived || aborted) return;
        // Treat as an abort so the drain/catch handlers below don't also fire.
        aborted = true;
        isSubscribed.value = false;
        abortController.abort();
        callbacks.onError?.('Export timed out — please try again');
    };
    const armInactivityTimer = () => {
        clearInactivityTimer();
        inactivityTimer = setTimeout(
            onInactivityTimeout,
            firstEventSeen
                ? EXPORT_STREAM_INACTIVITY_TIMEOUT_MS
                : EXPORT_STREAM_FIRST_EVENT_TIMEOUT_MS
        );
    };

    const connect = async () => {
        armInactivityTimer();
        const reader = await apiClient.postSSERequest(
            `/track_task/${taskRunId}`,
            {},
            abortController.signal
        );
        if (!reader) {
            // The watchdog abort also nulls the reader; it already reported the
            // timeout, so don't overwrite it with a misleading connect error.
            if (!aborted) {
                callbacks.onError?.('Failed to connect to export stream');
            }
            return;
        }

        await readStream(reader, isSubscribed, (jsonData) => {
            // A terminal event racing the watchdog timeout must not settle the
            // caller a second time after it already got the timeout failure.
            if (aborted) return;
            firstEventSeen = true;
            armInactivityTimer(); // any activity resets the watchdog
            if (!jsonData?.data) return;
            const data = jsonData.data;

            if (data.state === 'PROGRESS') {
                callbacks.onProgress?.({
                    step: data.step,
                    slideIndex: data.slide_index,
                    total: data.total
                });
            } else if (data.state === 'SUCCESS') {
                terminalReceived = true;
                isSubscribed.value = false;
                callbacks.onSuccess?.(data as SlideExportResult);
            } else if (data.state === 'ERROR') {
                terminalReceived = true;
                isSubscribed.value = false;
                callbacks.onError?.(data.error);
            }
        });

        // Stream fully drained. If it closed without a terminal event and the
        // caller did not abort, the export outcome is unknown — surface an error
        // so the awaiting promise settles (otherwise the UI hangs on "Generating…").
        if (!terminalReceived && !aborted) {
            callbacks.onError?.('Export stream closed before completion');
        }
    };

    connect()
        .catch((error) => {
            if (!terminalReceived && !aborted) {
                callbacks.onError?.(
                    error instanceof Error
                        ? error.message
                        : 'Export stream failed'
                );
            }
        })
        .finally(clearInactivityTimer);

    return {
        abort: () => {
            aborted = true;
            isSubscribed.value = false;
            clearInactivityTimer();
            abortController.abort();
        }
    };
}

export async function downloadSlideExport(
    exportId: string,
    tenantId: string,
    format: SlideExportFormat,
    filename: string
): Promise<void> {
    const params = new URLSearchParams({
        tenant_id: tenantId,
        format,
        filename
    });
    const url = `/slide_studio/exports/${encodeURIComponent(exportId)}/download?${params.toString()}`;
    const response = await apiClient.get(url);
    const blob = await response.blob();

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }, 100);
}
